import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import $ from '../utils/query';
import { Article, SubscriptionPeriodType } from '../../../models/Article';
import { isListProduct, Product, ProductMetaData } from '../../../models/Product';
import { GroupingState } from '../../../models/GroupItem';
import { SingleAddOptions, SingleCopyOrMoveOptions } from '../../../models/ListContentCheckOptions';
import Utils from '../utils';
import { ProductSelection } from '../product-selection/ProductSelectionTypes';
import { hasProductSelection, isSelected } from '../product-selection/ProductSelectionUtils';
import { LineItemKey } from '../../store/api';
import { DISPLAY_TYPE_LIST, DISPLAY_TYPE_MINIMAL, DisplayType } from './displayType/types';
import { LoanFormatState } from '../../app-store/loanFormat/loanFormatReducer';
import { concreteLoanFormatNameToKey } from '../loanFormat/mappers';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { usePreviousProps } from '../../hooks/usePreviousProps';
import { LendingModel } from '../../../models/PriceTier';
import { usePaginationAndSortParams } from '../hooks/usePaginationAndSortParams';

const TileContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

export type ProductListItemProps = {
    readonly actionButtons?: ReactNode;
    readonly hoverListener?: (hovered: boolean) => void;
    readonly isDeleteAction?: boolean;
    readonly isInPartnerContext: boolean;
    readonly isProductSelectable: boolean;
    readonly isProductSelected: boolean;
    readonly loanFormat: string;
    readonly minimalView: boolean;
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly onProductImageClick: (() => void) | undefined;
    readonly onSelectProduct: () => void;
    readonly product: ProductMetaData & Article;
    readonly productSelection: ProductSelection;
};

export type AdditionalItemRenderProps = {
    readonly key: string;
    readonly isListView: boolean;
};

type Props = {
    readonly children: (props: ProductListItemProps & AdditionalItemRenderProps) => ReactNode;
    readonly currentPage?: number;
    readonly displayType: DisplayType;
    readonly grouping?: GroupingState;
    readonly isProductSelectable?: boolean;
    readonly loading?: boolean;
    readonly loanFormatOverride?: LoanFormatState;
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly onSelectProduct?: (lineItemKey: LineItemKey) => void;
    readonly productSelection?: ProductSelection;
    readonly products: ReadonlyArray<Product>;
};

function ProductList(props: Props) {
    const [pageChanged, setPageChanged] = useState(false);
    let { page: currentPage } = usePaginationAndSortParams();
    currentPage = props.currentPage ?? currentPage;
    const prevCurrentPage = usePreviousProps(currentPage);
    const authentication = useAuthentication();

    useEffect(() => {
        if (prevCurrentPage !== undefined && prevCurrentPage !== currentPage) {
            setPageChanged(true);
        }
    }, [prevCurrentPage, currentPage]);

    useEffect(() => {
        if (props.products.length > 0 && pageChanged) {
            if (props.grouping?.id) {
                const itemDiv = $('.item-products')[0].parentElement;
                $.scrollTo(itemDiv, true);
                $.scrollDown(60);
            } else {
                $.scrollTo('.pagination');
            }
            setPageChanged(false);
        }
    }, [pageChanged, props.grouping?.id, props.products.length]);

    function onProductImageClick(lineItemKey: LineItemKey) {
        if (props.isProductSelectable && hasProductSelection(props.productSelection) && props.onSelectProduct) {
            return () => {
                props.onSelectProduct?.(lineItemKey);
            };
        }

        return undefined;
    }

    const isMinimal = props.displayType === DISPLAY_TYPE_MINIMAL;
    const isListView = props.displayType === DISPLAY_TYPE_LIST;

    const products = props.products.flatMap((product): (ProductListItemProps & AdditionalItemRenderProps)[] => {
        const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
        const loanFormatKey = props.loanFormatOverride ? props.loanFormatOverride.key : productLoanFormatKey;

        // Merge listitem to product.price
        if (isListProduct(product)) {
            return product.lineItems.map((lineItem): ProductListItemProps & AdditionalItemRenderProps => {
                const price = lineItem.price ?? {
                    lendingModel: undefined,
                    usageLimit: undefined,
                    termLimit: undefined
                };

                const lineItemKey = mapToLineItemKey({
                    id: product.id,
                    price,
                    subscriptionPeriodType: lineItem.subscriptionPeriod?.type
                });

                product = {
                    ...product,
                    price,
                    availablePrices: lineItem.availablePrices,
                    subscriptionPeriod: lineItem.subscriptionPeriod,
                    subscriptionPeriodType: lineItem.subscriptionPeriod?.type,
                    // FIXME: copyAmount should be part of the `price` property
                    ...({ copyAmount: lineItem.price?.copyAmount } as any),
                    lineItemKey
                };

                return {
                    product,
                    loanFormat: loanFormatKey,
                    onAddPrice: props.onAddPrice,

                    // defining a print order via the replacer param in the stringify method
                    // keeps the unique identifier key idempotent for each item
                    key: JSON.stringify(lineItemKey, [
                        'productId',
                        'usageCondition',
                        'lendingModel',
                        'usageLimit',
                        'termLimit',
                        'subscriptionPeriodType'
                    ]),
                    productSelection: props.isProductSelectable ? props.productSelection : undefined,
                    isProductSelectable: props.isProductSelectable ?? false,
                    onProductImageClick: onProductImageClick(lineItemKey),
                    onSelectProduct: () => {
                        props.onSelectProduct?.(lineItemKey);
                    },
                    isProductSelected: isSelected(lineItemKey, props.productSelection),
                    minimalView: isMinimal,
                    isInPartnerContext: Utils.isInPartnerContext(authentication),
                    isListView
                };
            });
        } else {
            const lineItemKey = mapToLineItemKey({ id: product.id });

            return [
                {
                    product,
                    onAddPrice: props.onAddPrice,
                    loanFormat: loanFormatKey,
                    isProductSelectable: props.isProductSelectable ?? false,
                    key: `${product.id}-${product.price?.lendingModel}-${product.price?.usageLimit}-${product.price?.termLimit}`,
                    productSelection: props.isProductSelectable ? props.productSelection : undefined,
                    onProductImageClick: onProductImageClick(lineItemKey),
                    onSelectProduct: () => {
                        props.onSelectProduct?.(lineItemKey);
                    },
                    isProductSelected: isSelected(lineItemKey, props.productSelection),
                    minimalView: isMinimal,
                    isInPartnerContext: Utils.isInPartnerContext(authentication),
                    isListView
                }
            ];
        }
    });
    const ContainerComponent = isListView ? 'div' : TileContainer;
    const className = classnames('overwrite-legacy-css-for-ProductList', {
        'product-tile-container-new': !isListView,
        'product-list-container-new': isListView,
        'min-h-screen': props.loading
    });

    return (
        <ContainerComponent className={className} data-displayname="ProductList" data-testid="ProductList">
            {products.map(props.children)}
        </ContainerComponent>
    );
}

function mapToLineItemKey({
    id: productId,
    price = undefined,
    subscriptionPeriodType = undefined
}: {
    id: string;
    price?: {
        lendingModel?: LendingModel;
        termLimit?: number;
        usageLimit?: number;
    };
    subscriptionPeriodType?: SubscriptionPeriodType;
}): LineItemKey {
    const usageCondition = price
        ? {
              lendingModel: price.lendingModel,
              usageLimit: price.usageLimit,
              termLimit: price.termLimit
          }
        : undefined;

    return { productId, usageCondition, subscriptionPeriodType };
}

export default ProductList;
